import { computed, defineComponent, getCurrentInstance, useContext } from '@nuxtjs/composition-api';
import { useTask } from 'vue-concurrency';
import { useDuck } from '~/services';
import { createGridChunks, getArticlesSize } from '~/composables';
import { presets } from '~/composables/grid/rubric';
import { cachePage, useAsyncRef, createHead } from '~/utils';
/**
 * Duck service.
 */
const duck = useDuck();
/**
 * Columns
 */
export const columns = {
    desktop: 2,
    desktopMedium: 2,
    desktopWide: 3,
    mobile: 1,
    mobileMedium: 1,
    mobileWide: 1,
    tablet: 2,
};
export default defineComponent({
    setup() {
        const context = useContext();
        const viewport = context.$viewport;
        /**
         * Instance.
         */
        const instance = getCurrentInstance();
        const isDone = useAsyncRef(instance, 'isDone');
        /**
         * Articles.
         */
        const articles = useAsyncRef(instance, 'articles');
        /**
         * Current preset.
         */
        const articlesCards = computed(() => getArticlesSize(viewport.breakpoint, {
            columns,
            presets,
        }));
        /**
         * Grids.
         */
        const grids = computed(() => createGridChunks(articles.value, articlesCards.value));
        const pageOffset = useAsyncRef(instance, 'pageOffset');
        /**
         * Fetch articles task.
         */
        const fetchArticlesTask = useTask(function* (signal) {
            if (fetchArticlesTask.isRunning || isDone.value) {
                return;
            }
            signal.addEventListener('abort', () => duck.abortRequest('getPubNew'));
            try {
                const { data: list } = yield duck.pub.getPubNew({
                    'list[limit]': articlesCards.value,
                    'list[offset]': articles.value.length * (pageOffset.value + 1),
                });
                if (list.length < articlesCards.value) {
                    isDone.value = true;
                }
                if (!list.length) {
                    return;
                }
                articles.value = [...articles.value, ...list];
            }
            catch (error) {
                console.error(error);
            }
        });
        function handleGridIntersect(gridKey) {
            gridKey = gridKey + 1 + pageOffset.value;
            history.replaceState(null, '', gridKey > 1 ? `?page=${gridKey}` : context.route.value.fullPath);
        }
        return {
            articlesCards,
            columns,
            fetchArticlesTask,
            grids,
            handleGridIntersect,
            isDone,
            presets,
        };
    },
    async asyncData(context) {
        const { app, env, error, req, res, route } = context;
        let isDone = false;
        const limit = getArticlesSize(app.$viewport.breakpoint, {
            columns,
            presets,
        });
        const pageNumber = Number(route.query.page) || 0;
        const pageOffset = pageNumber > 0 ? pageNumber - 1 : 0;
        try {
            const { data: articles } = await duck.pub.getPubNew({
                'list[limit]': limit,
                'list[offset]': limit * pageOffset,
            });
            if (pageNumber && !articles.length) {
                throw new Error('Not found');
            }
            if (articles.length < limit) {
                isDone = true;
            }
            if (process.server) {
                const [firstArticle] = articles;
                if (firstArticle) {
                    const date = firstArticle.changed || firstArticle.time || firstArticle.created;
                    cachePage({ date, req, res });
                }
            }
            const title = 'Новое';
            const head = {
                canonical: env.HOST + route.path,
                title,
                url: env.HOST + route.path,
            };
            context.$page = app.$page = {
                title,
            };
            return {
                $page: context.$page,
                articles,
                head,
                isDone,
                pageOffset,
            };
        }
        catch (e) {
            console.error(e);
            error({ statusCode: 404 });
        }
    },
    head() {
        return createHead(this.head);
    },
});
